import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { applyConsent, flushNow, markInteracted, readConsent, trackEvent } from "@/lib/analytics";

const QUOTE_LINK = /\/(nl|en|fr)\/offerte\/?$/;

function locationOf(el: Element): string {
  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  return "page";
}

/** Global delegated listeners: page views, clicks, forms, scroll depth. Client-only. */
export function AnalyticsTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lastLang = useRef<string | null>(null);
  const scrollMarks = useRef(new Set<number>());

  useEffect(() => {
    applyConsent(readConsent());
    const onInteract = () => markInteracted();
    const onClick = (e: MouseEvent) => {
      markInteracted();
      const el = (e.target as Element | null)?.closest("a,button,[data-track]");
      if (!el) return;
      const href = el.getAttribute("href") ?? "";
      const where = locationOf(el);
      if (href.startsWith("tel:")) return trackEvent("click_phone", { label: where });
      if (href.startsWith("mailto:")) return trackEvent("click_email", { label: where });
      if (/wa\.me|whatsapp/.test(href)) return trackEvent("click_whatsapp", { label: where });
      const tracked = el.getAttribute("data-track");
      if (tracked) return trackEvent("button_click", { label: tracked, props: { location: where } });
      if (QUOTE_LINK.test(href)) return trackEvent("button_click", { label: "quote", props: { location: where } });
    };
    const started = new WeakSet<HTMLFormElement>();
    const submitted = new WeakSet<HTMLFormElement>();
    const onInput = (e: Event) => {
      markInteracted();
      const form = (e.target as Element | null)?.closest("form");
      if (form && !started.has(form)) {
        started.add(form);
        trackEvent("form_start", { label: form.getAttribute("aria-label") ?? form.id ?? null });
      }
    };
    const onSubmit = (e: Event) => submitted.add(e.target as HTMLFormElement);
    const onHide = () => {
      if (document.visibilityState !== "hidden") return;
      document.querySelectorAll("form").forEach((f) => {
        if (started.has(f) && !submitted.has(f)) trackEvent("form_abandon", { label: f.getAttribute("aria-label") ?? f.id ?? null });
      });
      flushNow();
    };
    let scrollTimer: ReturnType<typeof setTimeout> | undefined;
    const measureScroll = () => {
      scrollTimer = undefined;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = (window.scrollY / max) * 100;
      for (const mark of [25, 50, 75, 90]) {
        if (pct >= mark && !scrollMarks.current.has(mark)) {
          scrollMarks.current.add(mark);
          trackEvent("scroll_depth", { label: String(mark) });
        }
      }
    };
    const onScroll = () => {
      if (scrollTimer === undefined) scrollTimer = setTimeout(measureScroll, 150);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("input", onInput, true);
    document.addEventListener("submit", onSubmit, true);
    document.addEventListener("keydown", onInteract, { passive: true });
    document.addEventListener("pointermove", onInteract, { passive: true, once: true });
    document.addEventListener("touchstart", onInteract, { passive: true });
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("submit", onSubmit, true);
      document.removeEventListener("keydown", onInteract);
      document.removeEventListener("pointermove", onInteract);
      document.removeEventListener("touchstart", onInteract);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("scroll", onScroll);
      if (scrollTimer !== undefined) clearTimeout(scrollTimer);
    };
  }, []);

  useEffect(() => {
    scrollMarks.current = new Set();
    const lang = /^\/(nl|en|fr)(?=\/|$)/.exec(pathname)?.[1] ?? null;
    if (lastLang.current && lang && lang !== lastLang.current) {
      trackEvent("language_switch", { label: `${lastLang.current}->${lang}` });
    }
    lastLang.current = lang;
    trackEvent("page_view");
  }, [pathname]);

  return null;
}
